import React, { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CustomSpinner } from '@yakoffice/custom-spinner';
import { useBuilds } from '../../loaders/use-builds';
import BuildTypeFilterSelector, { BuildTypeFilter } from '../components/buildFilter/buildTypeFilterSelector';
import BuildsHeader from '../components/buildHeader/buildsHeader';
import BuildCard from '../buildCard/buildCard';
import Alert from 'react-bootstrap/cjs/Alert';
import styles from './builds.module.sass';
import Container from 'react-bootstrap/cjs/Container';
import { useParams } from 'react-router-dom';
import { useToolAuthorisationClaimsContext } from '../../authentication/ToolAuthorisationClaimsContext';
import Row from 'react-bootstrap/cjs/Row';
import { useAuthUser } from '@yakoffice/yakoffice-firebase';
import { useBuildsController } from '../../controllers/useBuildsController';
import { DocumentTitle, ProjectName } from '@yakoffice/shared-components';
import BuildIdSearchInput from '../components/buildHeader/buildSearch/buildIdSearchInput';


export const Builds: FC<{ isMobile: boolean }> = observer((props) => {

    const getBuildsType = (): BuildTypeFilter => {

        const buildsTypeDataCache = localStorage.getItem(projectSpecificBuildTypeDataCacheKey);

        if (buildsTypeDataCache === 'null' || buildsTypeDataCache === undefined)
            return BuildTypeFilter.All;

        else
            return buildsTypeDataCache as BuildTypeFilter;
    };

    const authUser = useAuthUser();
    const toolAuthorisationClaimsContext = useToolAuthorisationClaimsContext();
    const buildsController = useBuildsController();
    const { buildId } = useParams<{ buildId: string }>();
    const { projectId } = useParams<{ projectId: string }>();
    const projectSpecificBuildTypeDataCacheKey = `buildsTypeDataCacheFor${projectId}`;
    const [actionTrigger, setActionTrigger] = useState<number>(0);
    const [buildIdSearchVal, setBuildIdSearchVal] = useState<string>(buildId !== undefined ? buildId : null);
    const [buildIdSearchVisibility, setBuildIdSearchVisibility] = useState<boolean>(!!buildIdSearchVal);
    const [buildsType, setBuildsType] = useState<BuildTypeFilter>(getBuildsType());
    const [scrolled, setScrolled] = useState(false);

    const buildsResult = useBuilds({
        miniclipPublicAccess: toolAuthorisationClaimsContext.MiniclipPublicAccess,
        buildsType: buildsType,
        buildId: buildIdSearchVal,
        userEmail: authUser.firebaseUser.email,
        event: actionTrigger
    });

    useEffect(() => {
        buildId || buildIdSearchVal ? DocumentTitle(buildId + ' - Builds - ' + ProjectName(projectId)) : DocumentTitle('Builds - ' + ProjectName(projectId));
        window.localStorage.setItem(projectSpecificBuildTypeDataCacheKey, buildsType);
    }, [buildsType]);

    useEffect(() => {

        const handleScroll = () => {
            const offset = window.scrollY;

            if (offset > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const updateBuildsType = (newType: BuildTypeFilter) => setBuildsType(newType);

    const toggleSearchVisibility = () => setBuildIdSearchVisibility(!buildIdSearchVisibility);

    const updateActionTrigger = () => setActionTrigger(actionTrigger + 1);

    const searchOnBuildId = (buildId: string) => {
        setBuildIdSearchVal(buildId);
        setBuildIdSearchVisibility(!!buildId);
    };

    console.log(buildsResult);

    return (
        <div className={`${styles.projectContent} projectContent themed-project`}>

            {props.isMobile &&
                <Container id="stickyBuildsHeader"
                           className={`${styles.stickyContainer} ${scrolled ? styles.scrolled : ''}`}>

                    <BuildsHeader toggleSearchVisibility={toggleSearchVisibility}
                                  buildIdSearchVisibility={buildIdSearchVisibility}
                                  currentBuildTypeFilter={buildsType}
                                  updateBuildTypeFilter={updateBuildsType}
                                  buildIdSearchVal={buildIdSearchVal}
                                  searchOnBuildId={searchOnBuildId}
                                  disabled={!buildIdSearchVal}
                                  isMobile={props.isMobile}
                                  authUser={authUser}
                    />
                </Container>
            }

            <Container className={`${!props.isMobile && styles.stickyContainer}`}>
                <div className={styles.lowerHeader}>
                    <div className={styles.title}>Builds</div>
                    {!props.isMobile &&
                        <BuildIdSearchInput currentBuildId={buildIdSearchVal} searchOnBuildId={searchOnBuildId}
                                            searchVisibility={buildIdSearchVisibility}
                                            toggleSearchVisibility={toggleSearchVisibility} />}

                    <BuildTypeFilterSelector currentBuildTypeFilter={buildsType}
                                             updateBuildTypeFilter={updateBuildsType}
                                             disabled={!!buildIdSearchVal} />
                </div>
            </Container>
            <div className={styles.content}>
                {buildsResult.isLoaded
                    ? buildsResult.builds.length > 0
                        ? <Container>
                            <Row>
                                {buildsResult.builds.map(build =>
                                    <BuildCard build={build} key={build.id}
                                               expanded={buildsResult.builds.length === 1}
                                               handleMakeBuildPublic={(build) => buildsController.handleMakeBuildPublic(build, updateActionTrigger)}
                                               handleShareBuild={(build) => buildsController.handleShareBuild(build)}
                                               handleAddReleaseNotes={(build) => buildsController.handleAddReleaseNotes(build, updateActionTrigger)}
                                               handleClearReleaseNotes={(build) => buildsController.handleClearReleaseNotes(build, updateActionTrigger)} />
                                )}
                            </Row>
                        </Container>
                        : <Container fluid>
                            <Alert className="mt-4 status-alert" data-testid="alertNotFound">
                                <h3 className="d-block text-lg">No builds found</h3>
                                <i className="fas fa-tools" />
                            </Alert>
                        </Container>
                    : <CustomSpinner spinnerText="Loading builds..." textColor="text-light" />
                }
            </div>
        </div>
    );
});


